import {
  useRefreshDisplayRightsSurfaces,
  useReviseDisplayRightsProposal,
  useSendDisplayRightsProposal,
} from '@/hooks/actions/use-display-rights-proposal-actions';
import { useDisplayRightsCompositionQuery } from '@/hooks/queries/use-display-rights-composition-query';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DisplayRightsDeliveryChannel,
  DisplayRightsProposalStatus,
  type DisplayRightsCompositionContextResponse,
  type SendDisplayRightsProposalResponse,
} from '@fluenceur/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  DISPLAY_RIGHTS_DEFAULT_DURATION_MONTHS,
  buildDisplayRightsDefaults,
  displayRightsComposerSchema,
  toDisplayRightsTermsPayload,
  type DisplayRightsComposerValues,
} from './display-rights-composer-schema';
import { resolveDisplayRightsStatus } from './display-rights-status';

interface UseDisplayRightsComposerOptions {
  authorizationId: string;
  /** Personalises the pre-filled message; omitted where no handle is at hand. */
  creatorHandle?: string | null;
  /**
   * Opening amount and run time for a NEW chain, when the caller brings its
   * own. Ignored on a revision, where a version is already in play.
   */
  initialTerms?: { amountCents: number; durationMonths: number } | null;
}

/**
 * A version already sent and still awaiting an answer is the only case that
 * REPLACES: the old version is superseded and a new link is issued, which is
 * what the confirmation warns about and what routes the call to the revision
 * endpoint.
 *
 * A residual `draft` also reports `revise` (the slot is occupied) but nothing
 * was ever delivered, so there is nothing to invalidate and nobody to warn: it
 * goes through the plain send endpoint, which adopts the orphan.
 */
function isReplacement(context: DisplayRightsCompositionContextResponse): boolean {
  return (
    context.action === 'revise' && context.current?.status === DisplayRightsProposalStatus.SENT
  );
}

/**
 * Whether the brand has to be warned before replacing.
 *
 * Only a version still WITHIN its acceptance window is worth a warning: there is
 * a live offer, and a live link, that the replacement destroys. Once the window
 * has passed there is nothing left to invalidate — warning about "the link you
 * already sent becomes invalid" would name a destruction that already happened,
 * and would read as a threat instead of a fact. The call still routes through
 * the revision endpoint either way; only the ceremony changes.
 */
function needsReplacementWarning(context: DisplayRightsCompositionContextResponse): boolean {
  if (!isReplacement(context) || !context.current) {
    return false;
  }

  return resolveDisplayRightsStatus(context.current) !== DisplayRightsProposalStatus.EXPIRED;
}

/**
 * The composer's whole state machine: fetch the gesture the server allows,
 * pre-fill accordingly, gate a replacement behind an explicit confirmation, and
 * send through the endpoint that matches the gesture.
 *
 * The endpoint is never chosen from the displayed status — only from the
 * server's `action`, so a stale screen cannot turn a renewal into a revision.
 */
export function useDisplayRightsComposer({
  authorizationId,
  creatorHandle,
  initialTerms,
}: UseDisplayRightsComposerOptions) {
  const { t } = useTranslation();
  const query = useDisplayRightsCompositionQuery(authorizationId);
  const sendProposal = useSendDisplayRightsProposal();
  const reviseProposal = useReviseDisplayRightsProposal();
  const [result, setResult] = useState<SendDisplayRightsProposalResponse | null>(null);
  const [hasConfirmedReplacement, setHasConfirmedReplacement] = useState(false);

  const defaultNote = creatorHandle
    ? t('displayRights.composer.messageDefault', { handle: creatorHandle })
    : t('displayRights.composer.messageDefaultAnonymous');

  const form = useForm<DisplayRightsComposerValues>({
    resolver: zodResolver(displayRightsComposerSchema),
    defaultValues: {
      amount: '',
      durationMonths: DISPLAY_RIGHTS_DEFAULT_DURATION_MONTHS,
      note: '',
      channel: DisplayRightsDeliveryChannel.LINK_COPY,
    },
  });

  // Defaults depend on the fetched context, so they land once it does — and
  // exactly once: a background refetch must never wipe what the brand typed.
  const context = query.data;
  const initializedFor = useRef<string | null>(null);
  const { reset } = form;
  useEffect(() => {
    if (!context || initializedFor.current === context.authorizationId) {
      return;
    }
    initializedFor.current = context.authorizationId;
    reset(buildDisplayRightsDefaults(context, defaultNote, initialTerms));
  }, [context, defaultNote, initialTerms, reset]);

  // Opening the composer is the moment the brand learns the authorization was
  // withdrawn — but the row it clicked from still shows the old state, because a
  // read never invalidated anything (only mutations did). So when the context
  // comes back saying nothing can be composed AND no agreement is running, the
  // surfaces are stale by definition: refresh them once.
  //
  // Once, guarded by a ref on the authorization: invalidating the lists cannot
  // change this query's answer, so there is no loop, but an unguarded effect
  // would refetch on every render of a closed authorization.
  // Excludes this very query: invalidating it here re-fetched the context that
  // had just answered, one full GET per opening on a closed authorization.
  const refreshStaleSurfaces = useRefreshDisplayRightsSurfaces({ includeComposition: false });
  const refreshedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!context || context.action !== 'none' || refreshedFor.current === context.authorizationId) {
      return;
    }

    // The server says WHY, so nothing is re-derived here: a running agreement is
    // not stale news, a withdrawn authorization is.
    if (context.actionBlocker !== 'authorization_closed') {
      return;
    }

    refreshedFor.current = context.authorizationId;
    refreshStaleSurfaces();
  }, [context, refreshStaleSurfaces]);

  const needsReplacementConfirmation =
    !!context && needsReplacementWarning(context) && !hasConfirmedReplacement;

  const submit = form.handleSubmit((values) => {
    if (!context) {
      return;
    }

    const terms = toDisplayRightsTermsPayload(values);

    if (isReplacement(context) && context.current) {
      reviseProposal.mutate({ proposalId: context.current.id, ...terms }, { onSuccess: setResult });
      return;
    }

    sendProposal.mutate({ authorizationId, ...terms }, { onSuccess: setResult });
  });

  const confirmReplacement = useCallback(() => setHasConfirmedReplacement(true), []);

  return {
    context,
    form,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    needsReplacementConfirmation,
    confirmReplacement,
    isSubmitting: sendProposal.isPending || reviseProposal.isPending,
    result,
    submit,
  };
}
